import type {
  Assessment,
  Patient,
  PatientsListResponse,
} from "../../../domain/entities/Patient";
import type {
  AssessmentListItemDTO,
  PatientDTO,
  PatientsListResponseDTO,
} from "../../../domain/dto/PatientDTO";
import { formatRelativeTime } from "@/lib/dateUtils";

const getInitials = (name: string): string =>
  name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

const getPatientLanguage = (dto: PatientDTO): string | undefined => {
  if (typeof dto.language === "string") return dto.language;
  if (dto.language?.name) return dto.language.name;
  if (dto.language?.language) return dto.language.language;
  return dto.language_name ?? dto.patient_language ?? undefined;
};

export const mapPatientDTO = (dto: PatientDTO): Patient => ({
  id: dto.id,
  instituteId: dto.institute_id,
  addedBy: dto.added_by,
  name: dto.name,
  gender: dto.gender,
  phone: dto.phone,
  email: dto.email,
  language: getPatientLanguage(dto),
  ethnicity: dto.ethnicity,
  caregiverName: dto.caregiver_name,
  caregiverNumber: dto.caregiver_number,
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
  dateOfBirth: dto.date_of_birth,
  initials: getInitials(dto.name),
  formattedCreatedAt: formatRelativeTime(dto.created_at),
});

export const mapPatientsDTO = (dtos: PatientDTO[]): Patient[] =>
  dtos.map(mapPatientDTO);

export const mapPatientsListResponseDTO = (
  response: PatientsListResponseDTO,
): PatientsListResponse => ({
  patients: mapPatientsDTO(response.data.patients),
  meta: response.meta,
});

export const mapAssessmentDTO = (dto: AssessmentListItemDTO): Assessment => ({
  id: dto.id,
  isCurrentAssessment: dto.is_current_assessment,
});

export const mapAssessmentsListResponseDTO = (
  items: AssessmentListItemDTO[],
): Assessment[] => items.map(mapAssessmentDTO);
